import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Camera, X } from "lucide-react";
import PhotoEvolutionPanel from "@/components/photos/PhotoEvolutionPanel";

export default function Photos() {
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [preview, setPreview] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const fetchPhotos = () => { api.get('/photos').then(r => setPhotos(r.data)).catch(() => {}); };
  useEffect(() => { fetchPhotos(); }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 5MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreview(ev.target.result);
      setImageData(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!imageData) return toast.error("Sélectionnez une image");
    setLoading(true);
    try {
      await api.post('/photos', { image_data: imageData, caption });
      toast.success("Photo ajoutée");
      setOpen(false);
      setPreview(null);
      setImageData(null);
      setCaption("");
      fetchPhotos();
    } catch (err) {
      toast.error("Erreur lors de l'upload");
    } finally { setLoading(false); }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    await api.delete(`/photos/${photoId}`);
    toast.success("Photo supprimée");
    fetchPhotos();
  };

  return (
    <div data-testid="photos-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Photos</h1>
          <p className="text-slate-500 mt-1">Partagez et comparez vos photos de progression</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full" data-testid="upload-photo-btn">
              <Plus className="w-4 h-4 mr-2" /> Ajouter une photo
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading text-xl">Ajouter une photo</DialogTitle></DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4 mt-4">
              <div>
                <input type="file" accept="image/*" ref={fileRef} onChange={handleFileChange} className="hidden" />
                {preview ? (
                  <div className="relative">
                    <img src={preview} alt="Preview" className="w-full h-48 object-cover rounded-xl" />
                    <Button type="button" variant="ghost" size="icon" className="absolute top-2 right-2 bg-white/80" onClick={() => { setPreview(null); setImageData(null); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileRef.current?.click()} className="w-full h-48 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:border-blue-300 hover:text-blue-500 transition-colors" data-testid="photo-dropzone">
                    <Camera className="w-8 h-8 mb-2" />
                    <span className="text-sm">Cliquez pour sélectionner</span>
                  </button>
                )}
              </div>
              <div>
                <Input value={caption} onChange={e => setCaption(e.target.value)} placeholder="Légende (optionnel)" className="h-11" data-testid="photo-caption-input" />
              </div>
              <Button type="submit" disabled={loading || !imageData} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11" data-testid="submit-photo-btn">
                {loading ? "Upload..." : "Envoyer"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6">
        <PhotoEvolutionPanel
          photos={photos}
          showDelete
          onDelete={handleDelete}
          emptyMessage="Aucune photo. Ajoutez vos photos de progression !"
        />
      </div>
    </div>
  );
}